import { SyntheticEvent, useCallback, useRef } from 'react'

import { Editor } from 'tinymce'

type InsertAtCursor = (current: string, text: string) => string

/**
 * Insere um texto (ex.: variável `{{nome_cliente}}`) na posição do cursor de
 * um textarea controlado, em vez de concatenar no fim.
 *
 * O `Textarea` da library não repassa `ref`, então o elemento é capturado pelo
 * `onSelect` (disparado a cada clique/digitação/seleção). O textarea guarda a
 * seleção mesmo depois de perder o foco para o botão "Adicionar variável".
 * Se o usuário nunca posicionou o cursor, mantém o comportamento antigo
 * (insere no fim).
 */
export function useInsertAtCursor() {
  const fieldRef = useRef<HTMLTextAreaElement | null>(null)

  const trackCursor = useCallback(
    (event: SyntheticEvent<HTMLTextAreaElement>) => {
      fieldRef.current = event.currentTarget
    },
    []
  )

  const insertAtCursor = useCallback<InsertAtCursor>((current, text) => {
    const field = fieldRef.current
    if (!field?.isConnected) return current + text

    const start = Math.min(
      field.selectionStart ?? current.length,
      current.length
    )
    const end = Math.min(field.selectionEnd ?? start, current.length)
    const caret = start + text.length

    // Devolve o foco com o cursor logo após a variável, depois que o form
    // re-renderizar o novo valor (escrever `value` joga o cursor pro fim).
    setTimeout(() => {
      field.focus()
      field.setSelectionRange(caret, caret)
    })

    return current.slice(0, start) + text + current.slice(end)
  }, [])

  return { trackCursor, insertAtCursor }
}

/**
 * Mesma ideia do `useInsertAtCursor`, para o `SimpleRichTextEditor` (TinyMCE):
 * insere no cursor do editor. Se o usuário ainda não clicou no editor, anexa
 * no fim como antes — sem isso a variável cairia no início do texto.
 */
export function useRichTextInsertAtCursor() {
  const editorRef = useRef<Editor | null>(null)
  const touchedRef = useRef(false)

  const onEditorInit = useCallback((editor: Editor) => {
    editorRef.current = editor
    touchedRef.current = false
    editor.on('focus', () => {
      touchedRef.current = true
    })
  }, [])

  const insertAtCursor = useCallback<InsertAtCursor>((current, text) => {
    const editor = editorRef.current
    if (!editor || editor.removed || !touchedRef.current) return current + text

    // `focus()` restaura a última seleção do editor antes de inserir.
    editor.focus()
    editor.insertContent(text)
    return editor.getContent()
  }, [])

  return { onEditorInit, insertAtCursor }
}
